/**
 * NarrativeRenderer.ts — Story text renderer with formatting and effects.
 *
 * Handles the presentation of narrative text in Gallowcrest:
 * - Word wrapping at terminal width with paragraph detection
 * - Inline color markup: {color|text} for NPC names, locations, items
 * - Typewriter character-by-character reveal (skippable)
 * - Paging with "more" prompt for text exceeding viewport
 */

import type { Terminal } from './Terminal';
import type { InputManager } from './InputManager';
import type { ColorName } from './ColorTheme';

// --- Types ---

export interface TextSegment {
  text: string;
  color?: ColorName;
}

export type WrappedLine = TextSegment[];

export interface NarrativeOptions {
  /** Use typewriter reveal effect (default: false) */
  typewriter?: boolean;
  /** Typewriter speed in ms per character (default: 20) */
  typewriterSpeed?: number;
  /** Left indent in spaces (default: 2) */
  indent?: number;
  /** Base text color (default: 'white') */
  color?: ColorName;
  /** Max lines before "more" prompt (0 = auto from terminal height) */
  maxLines?: number;
}

// --- NarrativeRenderer ---

export class NarrativeRenderer {
  private terminal: Terminal;
  private inputManager: InputManager;

  // Text margins for better readability and word-wrap safety
  private readonly TEXT_MARGIN_LEFT = 2;
  private readonly TEXT_MARGIN_RIGHT = 4;

  constructor(terminal: Terminal, inputManager: InputManager) {
    this.terminal = terminal;
    this.inputManager = inputManager;
  }

  /**
   * Render narrative text with word wrapping, paragraph breaks,
   * inline color markup, optional typewriter effect, and paging.
   *
   * Color markup syntax: {color|text}
   * Example: "The wizard {blue|Lord Aldric} spoke softly."
   *
   * Supported colors: white, yellow, green, red, cyan, magenta, blue, gray, brightWhite
   */
  async render(text: string, options: NarrativeOptions = {}): Promise<void> {
    const {
      typewriter = false,
      typewriterSpeed = 20,
      indent = this.TEXT_MARGIN_LEFT,
      color = 'white',
      maxLines = 0,
    } = options;

    const { cols, rows } = this.terminal.getSize();
    // Apply both left and right margins for word-wrap safety
    const wrapWidth = cols - indent - this.TEXT_MARGIN_RIGHT;
    const padding = ' '.repeat(indent);
    const linesPerPage = maxLines > 0 ? maxLines : rows - 2;

    const allLines = this.prepareLines(text, color, wrapWidth);

    if (typewriter) {
      await this.typewriterRender(allLines, padding, typewriterSpeed, linesPerPage);
    } else {
      await this.pagedRender(allLines, padding, linesPerPage);
    }
  }

  /**
   * Render a single line with inline color markup (no paging).
   * Convenience method for short formatted output.
   */
  renderLine(text: string, color: ColorName = 'white', indent = this.TEXT_MARGIN_LEFT): void {
    const padding = ' '.repeat(indent);
    const { cols } = this.terminal.getSize();
    const segments = this.parseMarkup(text, color);
    // Apply both left and right margins for word-wrap safety
    const wrapped = this.wrapSegments(segments, cols - indent - this.TEXT_MARGIN_RIGHT);
    for (const line of wrapped) {
      this.writeFormattedLine(line, padding);
    }
  }

  // --- Text Processing ---

  /** Split text into paragraphs, parse markup, and word-wrap. */
  private prepareLines(text: string, baseColor: ColorName, wrapWidth: number): WrappedLine[] {
    const paragraphs = text.split(/\n\n+/);
    const allLines: WrappedLine[] = [];

    for (let i = 0; i < paragraphs.length; i++) {
      const para = paragraphs[i].replace(/\n/g, ' ').trim();
      if (!para) continue;

      const segments = this.parseMarkup(para, baseColor);
      const wrapped = this.wrapSegments(segments, wrapWidth);
      allLines.push(...wrapped);

      if (i < paragraphs.length - 1) {
        allLines.push([]); // blank line between paragraphs
      }
    }

    return allLines;
  }

  /** Parse {color|text} markup into text segments. */
  parseMarkup(text: string, baseColor: ColorName): TextSegment[] {
    const segments: TextSegment[] = [];
    const regex = /\{(\w+)\|([^}]+)\}/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        segments.push({ text: text.slice(lastIndex, match.index), color: baseColor });
      }
      segments.push({ text: match[2], color: match[1] as ColorName });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      segments.push({ text: text.slice(lastIndex), color: baseColor });
    }

    return segments;
  }

  /** Word-wrap text segments to fit within maxWidth columns. */
  wrapSegments(segments: TextSegment[], maxWidth: number): WrappedLine[] {
    // Flatten segments into individual words, preserving color
    const words: TextSegment[] = [];
    for (const segment of segments) {
      const parts = segment.text.split(/\s+/);
      for (const part of parts) {
        if (part.length > 0) {
          words.push({ text: part, color: segment.color });
        }
      }
    }

    if (words.length === 0) return [[]];

    const lines: WrappedLine[] = [[]];
    let currentWidth = 0;

    for (const word of words) {
      // Handle words longer than the line width
      if (word.text.length > maxWidth) {
        let remaining = word.text;
        while (remaining.length > 0) {
          if (currentWidth >= maxWidth) {
            lines.push([]);
            currentWidth = 0;
          }
          const available = maxWidth - currentWidth;
          const chunk = remaining.slice(0, available);
          lines[lines.length - 1].push({ text: chunk, color: word.color });
          currentWidth += chunk.length;
          remaining = remaining.slice(available);
          if (remaining.length > 0) {
            lines.push([]);
            currentWidth = 0;
          }
        }
        continue;
      }

      // Check if word fits with a preceding space
      const spaceNeeded = currentWidth > 0 ? 1 : 0;
      if (currentWidth + spaceNeeded + word.text.length > maxWidth) {
        lines.push([]);
        currentWidth = 0;
      }

      // Add space separator between words on the same line
      if (currentWidth > 0) {
        lines[lines.length - 1].push({ text: ' ', color: word.color });
        currentWidth += 1;
      }

      lines[lines.length - 1].push({ text: word.text, color: word.color });
      currentWidth += word.text.length;
    }

    return lines;
  }

  // --- Rendering ---

  /** Write a formatted line (segments with colors) to the terminal. */
  private writeFormattedLine(line: WrappedLine, padding: string): void {
    this.terminal.write(padding);
    for (const segment of line) {
      this.terminal.write(segment.text, segment.color);
    }
    this.terminal.blankLine();
  }

  /** Render all lines with paging for long text. */
  private async pagedRender(
    lines: WrappedLine[], padding: string, linesPerPage: number
  ): Promise<void> {
    let linesOnPage = 0;

    for (let i = 0; i < lines.length; i++) {
      this.writeFormattedLine(lines[i], padding);
      linesOnPage++;

      if (linesOnPage >= linesPerPage && i < lines.length - 1) {
        await this.morePrompt();
        linesOnPage = 0;
      }
    }
  }

  /** Render with character-by-character typewriter effect (skippable). */
  private async typewriterRender(
    lines: WrappedLine[], padding: string, speed: number, linesPerPage: number
  ): Promise<void> {
    let skipped = false;
    let linesOnPage = 0;

    const previousMode = this.inputManager.getMode();
    this.inputManager.setMode('anyKey');

    const skipHandler = () => {
      skipped = true;
      this.inputManager.off(skipHandler);
    };
    this.inputManager.on(skipHandler);

    for (let li = 0; li < lines.length; li++) {
      // Check paging
      if (linesOnPage >= linesPerPage && li < lines.length) {
        this.inputManager.off(skipHandler);
        this.inputManager.setMode(previousMode);
        try {
          await this.morePrompt();
        } catch (err) {
          console.warn('NarrativeRenderer morePrompt failed:', err);
          skipped = true;
        }
        linesOnPage = 0;

        if (!skipped) {
          this.inputManager.setMode('anyKey');
          this.inputManager.on(skipHandler);
        }
      }

      const line = lines[li];

      if (skipped) {
        this.writeFormattedLine(line, padding);
        linesOnPage++;
        continue;
      }

      // Character-by-character reveal
      this.terminal.write(padding);
      for (const segment of line) {
        if (skipped) {
          this.terminal.write(segment.text, segment.color);
          continue;
        }
        for (let ci = 0; ci < segment.text.length; ci++) {
          if (skipped) {
            this.terminal.write(segment.text.slice(ci), segment.color);
            break;
          }
          this.terminal.write(segment.text[ci], segment.color);
          await new Promise(r => setTimeout(r, speed));
        }
      }
      this.terminal.blankLine();
      linesOnPage++;
    }

    this.inputManager.off(skipHandler);
    this.inputManager.setMode(previousMode);
  }

  /** Show "-- more --" prompt and wait for keypress. */
  private morePrompt(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.terminal.write('  -- more --', 'frostSilver');

      const previousMode = this.inputManager.getMode();
      this.inputManager.setMode('anyKey');

      const handler = () => {
        this.inputManager.off(handler);
        this.inputManager.setMode(previousMode);
        this.terminal.writeRaw('\r\x1b[2K');
        resolve();
      };

      this.inputManager.on(handler);
    });
  }
}
